import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from '../hooks/use-toast';
import { ArrowRight, ArrowLeft, Loader2, Check, Search } from 'lucide-react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SellCarPageOptimal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [fetchingCarData, setFetchingCarData] = useState(false);
  const [carDataFetched, setCarDataFetched] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  
  const [formData, setFormData] = useState({
    // Steg 1: Kontaktinfo
    name: '',
    email: '',
    phone: '',
    // Steg 2: Bilinfo
    regNumber: '',
    kilometers: '',
    brand: '',
    model: '',
    year: '',
    fuelType: '',
    damages: '',
    // Steg 3: Vilkår
    acceptTerms: false,
    acceptNewsletter: false
  });

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (location.state) {
      setFormData(prev => ({
        ...prev,
        regNumber: location.state.regNumber || '',
        kilometers: location.state.kilometers || ''
      }));
      
      // DON'T skip to step 2 - always start at step 1 to collect contact info
      // The reg number and kilometers will be pre-filled when user reaches step 2
    }
  }, [location.state]);

  const fetchCarDetails = async (regNumber) => {
    if (regNumber.length < 5) return;
    
    setFetchingCarData(true);
    
    setTimeout(() => {
      const mockCarData = {
        brand: 'Toyota',
        model: 'Corolla',
        year: '2018',
        fuelType: 'Bensin'
      };
      
      setFormData(prev => ({
        ...prev,
        brand: mockCarData.brand,
        model: mockCarData.model,
        year: mockCarData.year,
        fuelType: mockCarData.fuelType
      }));
      
      setCarDataFetched(true);
      setFetchingCarData(false);
      
      toast({
        title: "Bildetaljer hentet!",
        description: `${mockCarData.brand} ${mockCarData.model} ${mockCarData.year}`
      });
    }, 1500);
  };

  const handleNextStep = () => {
    // Validate current step
    if (currentStep === 1) {
      if (!formData.name || !formData.email || !formData.phone) {
        toast({
          title: "Fyll ut alle feltene",
          variant: "destructive"
        });
        return;
      }
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast({
          title: "Ugyldig e-post",
          variant: "destructive"
        });
        return;
      }
    }
    
    if (currentStep === 2) {
      if (!formData.regNumber || !formData.kilometers) {
        toast({
          title: "Fyll ut reg.nr. og kilometer",
          variant: "destructive"
        });
        return;
      }
      
      if (!carDataFetched) {
        toast({
          title: "Hent bildetaljer først",
          variant: "destructive"
        });
        return;
      }
    }
    
    setCurrentStep(currentStep + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.acceptTerms) {
      toast({
        title: "Godta vilkårene",
        description: "Du må godta vilkårene for å fortsette",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/cars/submit`, {
        regNumber: formData.regNumber,
        kilometers: parseInt(formData.kilometers),
        brand: formData.brand,
        model: formData.model,
        year: parseInt(formData.year),
        fuelType: formData.fuelType,
        damages: formData.damages || undefined,
        ownerName: formData.name,
        ownerEmail: formData.email,
        ownerPhone: formData.phone,
        newsletter: formData.acceptNewsletter
      });
      
      // Navigate to thank you page with car data
      navigate('/takk', { 
        state: { 
          carData: {
            ...response.data,
            brand: formData.brand,
            model: formData.model,
            year: formData.year,
            regNumber: formData.regNumber,
            kilometers: formData.kilometers,
            fuelType: formData.fuelType
          }
        } 
      });
      
    } catch (error) {
      console.error('Error submitting car:', error);
      
      // Handle different error types from backend
      let errorMessage = "Noe gikk galt. Prøv igjen senere";
      
      if (error.response?.data) {
        const data = error.response.data;
        
        // Handle FastAPI validation errors (422)
        if (Array.isArray(data.detail)) {
          // Extract first error message
          errorMessage = data.detail[0]?.msg || errorMessage;
        } 
        // Handle string error messages (400, etc)
        else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
        // Handle other error formats
        else if (data.message) {
          errorMessage = data.message;
        }
      }
      
      toast({
        title: "Feil ved innsending",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            {[1, 2, 3].map((step, index) => (
              <React.Fragment key={step}>
                <div className={`flex flex-col items-center ${index > 0 ? 'ml-8' : ''}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                    currentStep >= step ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {currentStep > step ? <Check className="w-5 h-5" /> : step}
                  </div>
                  <span className={`text-xs mt-2 font-medium ${
                    currentStep >= step ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    {step === 1 ? 'Kontaktinfo' : step === 2 ? 'Om bilen' : 'Bekreft'}
                  </span>
                </div>
                {index < 2 && (
                  <div className={`w-24 h-1 mx-2 mt-[-20px] transition-colors ${
                    currentStep > step ? 'bg-gray-900' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <form onSubmit={handleSubmit}>
            
            {/* Step 1: Kontaktinfo */}
            <AnimatePresence mode="wait">
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Kontaktinformasjon</h2>
                    <p className="text-sm text-gray-600">Vi bruker dette for å kontakte deg med tilbud</p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Navn *
                      </label>
                      <Input
                        type="text"
                        placeholder="Ola Nordmann"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="h-12"
                        autoFocus
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        E-post *
                      </label>
                      <Input
                        type="email"
                        placeholder="ola@eksempel.no"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="h-12"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Telefon *
                      </label>
                      <Input
                        type="tel"
                        placeholder="123 45 678"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="h-12"
                      />
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={handleNextStep}
                    className="w-full h-12 bg-gray-900 hover:bg-gray-800"
                  >
                    Fortsett
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </motion.div>
              )}

              {/* Step 2: Om bilen */}
              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Om bilen</h2>
                    <p className="text-sm text-gray-600">Vi henter automatisk merke og modell</p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Reg.nr. *
                      </label>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="AB12345"
                          value={formData.regNumber}
                          onChange={(e) => {
                            setFormData({...formData, regNumber: e.target.value.toUpperCase()});
                            if (carDataFetched) setCarDataFetched(false);
                          }}
                          className="h-12 uppercase flex-1"
                          disabled={carDataFetched}
                          autoFocus={!formData.regNumber}
                        />
                        <Button
                          type="button"
                          onClick={() => fetchCarDetails(formData.regNumber)}
                          disabled={fetchingCarData || carDataFetched || formData.regNumber.length < 5}
                          className="h-12 px-6"
                          variant={carDataFetched ? "outline" : "default"}
                        >
                          {fetchingCarData ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : carDataFetched ? (
                            <>
                              <Check className="w-4 h-4 mr-2" />
                              Hentet
                            </>
                          ) : (
                            <>
                              <Search className="w-4 h-4 mr-2" />
                              Hent
                            </>
                          )}
                        </Button>
                      </div>
                      {carDataFetched && (
                        <p className="text-xs text-gray-500 mt-1">
                          <button type="button" onClick={() => setCarDataFetched(false)} className="underline">
                            Endre reg.nr.
                          </button>
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Kilometer *
                      </label>
                      <Input
                        type="number"
                        placeholder="150 000"
                        value={formData.kilometers}
                        onChange={(e) => setFormData({...formData, kilometers: e.target.value})}
                        className="h-12"
                      />
                    </div>

                    {carDataFetched && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="bg-gray-50 border border-gray-200 rounded-xl p-4"
                      >
                        <div className="flex items-center gap-2 text-xs font-medium text-gray-600 mb-3">
                          <Check className="w-3.5 h-3.5 text-green-600" />
                          Hentet automatisk
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500">Merke:</span>
                            <span className="ml-2 font-medium text-gray-900">{formData.brand}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Modell:</span>
                            <span className="ml-2 font-medium text-gray-900">{formData.model}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">År:</span>
                            <span className="ml-2 font-medium text-gray-900">{formData.year}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Drivstoff:</span>
                            <span className="ml-2 font-medium text-gray-900">{formData.fuelType}</span>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {carDataFetched && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                      >
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Kjente skader? (valgfritt)
                        </label>
                        <Textarea
                          placeholder="Beskriv eventuelle skader..."
                          value={formData.damages}
                          onChange={(e) => setFormData({...formData, damages: e.target.value})}
                          rows={3}
                          className="resize-none"
                        />
                      </motion.div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      onClick={handlePrevStep}
                      variant="outline"
                      className="flex-1 h-12"
                    >
                      <ArrowLeft className="mr-2 w-4 h-4" />
                      Tilbake
                    </Button>
                    <Button
                      type="button"
                      onClick={handleNextStep}
                      className="flex-1 h-12 bg-gray-900 hover:bg-gray-800"
                    >
                      Fortsett
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Bekreftelse og vilkår */}
              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Bekreft og send</h2>
                    <p className="text-sm text-gray-600">Sjekk at alt er riktig før du sender</p>
                  </div>

                  {/* Summary */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Din informasjon</h3>
                      <div className="space-y-1 text-sm">
                        <p className="text-gray-600">{formData.name}</p>
                        <p className="text-gray-600">{formData.email}</p>
                        <p className="text-gray-600">{formData.phone}</p>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Din bil</h3>
                      <div className="space-y-1 text-sm">
                        <p className="text-gray-900 font-medium">
                          {formData.brand} {formData.model} ({formData.year})
                        </p>
                        <p className="text-gray-600">{formData.regNumber}</p>
                        <p className="text-gray-600">{parseInt(formData.kilometers).toLocaleString('nb-NO')} km</p>
                        <p className="text-gray-600">{formData.fuelType}</p>
                      </div>
                    </div>
                  </div>

                  {/* Terms and Newsletter */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="terms"
                        checked={formData.acceptTerms}
                        onCheckedChange={(checked) => setFormData({...formData, acceptTerms: checked})}
                        className="mt-1"
                      />
                      <label htmlFor="terms" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                        Jeg godtar vilkårene og at DigiCar kan ta kontakt med meg angående salg av min bil. 
                        <a href="#" className="text-gray-900 underline ml-1">Les vilkår her</a>
                      </label>
                    </div>

                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="newsletter"
                        checked={formData.acceptNewsletter}
                        onCheckedChange={(checked) => setFormData({...formData, acceptNewsletter: checked})}
                        className="mt-1"
                      />
                      <label htmlFor="newsletter" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                        Ja, jeg vil gjerne abonnere på DigiCar sitt nyhetsbrev med tips og tilbud
                      </label>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      onClick={handlePrevStep}
                      variant="outline"
                      className="flex-1 h-12"
                    >
                      <ArrowLeft className="mr-2 w-4 h-4" />
                      Tilbake
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading || !formData.acceptTerms}
                      className="flex-1 h-12 bg-gray-900 hover:bg-gray-800"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sender...
                        </>
                      ) : (
                        'Kom i gang'
                      )}
                    </Button>
                  </div>

                  <p className="text-xs text-gray-500 text-center">
                    Vi kontakter deg innen 24 timer med tilbud fra forhandlere
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SellCarPageOptimal;
